import React, { useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CalendarRange,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  RotateCcw,
  UploadCloud,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { importMetasUl } from './api';
import { MetaUlImportEntry, MetaUlImportResponse } from './types';

type ImportModalProps = {
  onClose: () => void;
  onImported: (response: MetaUlImportResponse) => void;
};

type PreviewRow = MetaUlImportEntry & {
  sourceRow: number;
  periodLabel: string;
};

type ParsedImport = {
  entries: PreviewRow[];
  errors: string[];
  years: number[];
  periods: string[];
};

const BUSINESS_UNITS = ['HC', 'PC', 'BW', 'Foods'] as const;
const MONTH_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Mar\u00e7o',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const MONTH_BY_NAME: Record<string, number> = {
  jan: 1,
  janeiro: 1,
  fev: 2,
  fevereiro: 2,
  mar: 3,
  marco: 3,
  abr: 4,
  abril: 4,
  mai: 5,
  maio: 5,
  jun: 6,
  junho: 6,
  jul: 7,
  julho: 7,
  ago: 8,
  agosto: 8,
  set: 9,
  setembro: 9,
  out: 10,
  outubro: 10,
  nov: 11,
  novembro: 11,
  dez: 12,
  dezembro: 12,
};

function normalizeText(value: unknown) {
  return String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function normalizeHeader(value: unknown) {
  return normalizeText(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function normalizeBusinessUnit(value: unknown) {
  const normalized = normalizeText(value).toLowerCase();
  return BUSINESS_UNITS.find((businessUnit) => businessUnit.toLowerCase() === normalized) || null;
}

function parseMoney(value: unknown) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Number(value.toFixed(2)) : null;
  }

  let text = String(value ?? '').trim().replace(/R\$/gi, '').replace(/\s+/g, '');
  if (!text) return null;

  const commaPosition = text.lastIndexOf(',');
  const dotPosition = text.lastIndexOf('.');
  if (commaPosition >= 0 && dotPosition >= 0) {
    text = commaPosition > dotPosition
      ? text.replace(/\./g, '').replace(',', '.')
      : text.replace(/,/g, '');
  } else if (commaPosition >= 0) {
    text = text.replace(',', '.');
  }

  text = text.replace(/[^0-9.-]/g, '');
  if (!text || text === '-' || text === '.' || text === '-.') return null;

  const parsed = Number(text);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : null;
}

function parsePeriod(value: unknown): { year: number; month: number } | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { year: value.getFullYear(), month: value.getMonth() + 1 };
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const excelDate = XLSX.SSF.parse_date_code(value);
    if (excelDate?.y && excelDate?.m) return { year: excelDate.y, month: excelDate.m };
  }

  const normalized = normalizeText(value).toLowerCase();
  const isoMatch = normalized.match(/^(\d{4})[-/]([01]?\d)$/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    return month >= 1 && month <= 12 ? { year, month } : null;
  }

  const monthYearMatch = normalized.match(/^([a-z]+)[-/\s]+(\d{2}|\d{4})$/);
  if (!monthYearMatch) return null;

  const month = MONTH_BY_NAME[monthYearMatch[1]];
  const rawYear = Number(monthYearMatch[2]);
  const year = rawYear < 100 ? 2000 + rawYear : rawYear;
  if (!month || year < 2020 || year > 2200) return null;

  return { year, month };
}

function getPeriodLabel(year: number, month: number) {
  return `${MONTH_LABELS[month - 1]}/${year}`;
}

export function parseMetasUlWorkbook(workbook: XLSX.WorkBook): ParsedImport {
  const errors: string[] = [];
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return { entries: [], errors: ['A planilha n\u00e3o possui abas.'], years: [], periods: [] };

  const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[firstSheetName], {
    header: 1,
    raw: true,
    defval: null,
  });

  const headerIndex = rows.slice(0, 10).findIndex((row) => {
    const headers = row.map(normalizeHeader);
    return headers.includes('BU') && headers.includes('META') && headers.includes('MES');
  });
  if (headerIndex < 0) {
    return {
      entries: [],
      errors: ['Cabe\u00e7alhos obrigat\u00f3rios n\u00e3o encontrados. Use BU, META e M\u00caS.'],
      years: [],
      periods: [],
    };
  }

  const headers = rows[headerIndex].map(normalizeHeader);
  const buIndex = headers.indexOf('BU');
  const metaIndex = headers.indexOf('META');
  const periodIndex = headers.indexOf('MES');
  const entries: PreviewRow[] = [];
  const duplicateKeys = new Set<string>();

  rows.slice(headerIndex + 1).forEach((row, relativeIndex) => {
    const sourceRow = headerIndex + relativeIndex + 2;
    const values = [row[buIndex], row[metaIndex], row[periodIndex]];
    if (values.every((value) => value === null || value === undefined || String(value).trim() === '')) return;

    const businessUnit = normalizeBusinessUnit(row[buIndex]);
    const value = parseMoney(row[metaIndex]);
    const period = parsePeriod(row[periodIndex]);

    if (!businessUnit) errors.push(`Linha ${sourceRow}: BU inv\u00e1lida. Use HC, PC, BW ou Foods.`);
    if (value === null || value < 0) errors.push(`Linha ${sourceRow}: META deve ser um valor num\u00e9rico maior ou igual a zero.`);
    if (!period) errors.push(`Linha ${sourceRow}: M\u00caS inv\u00e1lido. Use o padr\u00e3o Out-25.`);
    if (!businessUnit || value === null || value < 0 || !period) return;

    const duplicateKey = `${period.year}|${period.month}|${businessUnit}`;
    if (duplicateKeys.has(duplicateKey)) {
      errors.push(`Linha ${sourceRow}: registro duplicado para ${businessUnit} em ${getPeriodLabel(period.year, period.month)}.`);
      return;
    }
    duplicateKeys.add(duplicateKey);

    entries.push({
      bu: businessUnit,
      year: period.year,
      month: period.month,
      value,
      sourceRow,
      periodLabel: getPeriodLabel(period.year, period.month),
    });
  });

  if (entries.length === 0 && errors.length === 0) errors.push('A planilha n\u00e3o possui registros para importar.');

  const unitsByPeriod = new Map<string, Set<string>>();
  entries.forEach((entry) => {
    const periodKey = `${entry.year}|${entry.month}`;
    const units = unitsByPeriod.get(periodKey) || new Set<string>();
    units.add(entry.bu);
    unitsByPeriod.set(periodKey, units);
  });

  unitsByPeriod.forEach((units, periodKey) => {
    const [year, month] = periodKey.split('|').map(Number);
    const missing = BUSINESS_UNITS.filter((businessUnit) => !units.has(businessUnit));
    if (missing.length > 0) {
      errors.push(`${getPeriodLabel(year, month)}: faltam as BUs ${missing.join(', ')}.`);
    }
  });

  entries.sort((left, right) => (
    left.year - right.year
    || left.month - right.month
    || BUSINESS_UNITS.indexOf(left.bu as typeof BUSINESS_UNITS[number])
      - BUSINESS_UNITS.indexOf(right.bu as typeof BUSINESS_UNITS[number])
  ));

  return {
    entries,
    errors,
    years: Array.from(new Set(entries.map((entry) => entry.year))).sort((a, b) => a - b),
    periods: Array.from(unitsByPeriod.keys()).sort(),
  };
}

function formatCurrency(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export const ImportModal = ({ onClose, onImported }: ImportModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [parsedImport, setParsedImport] = useState<ParsedImport | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [apiError, setApiError] = useState('');

  const totalsByYear = useMemo(() => {
    const totals = new Map<number, number>();
    parsedImport?.entries.forEach((entry) => totals.set(entry.year, (totals.get(entry.year) || 0) + 1));
    return Array.from(totals.entries());
  }, [parsedImport]);

  const processFile = async (file?: File) => {
    if (!file) return;
    setApiError('');
    setIsParsing(true);
    setFileName(file.name);

    try {
      if (!/\.(xlsx|xls)$/i.test(file.name)) throw new Error('Selecione um arquivo Excel .xlsx ou .xls.');
      if (file.size > 10 * 1024 * 1024) throw new Error('O arquivo deve ter no m\u00e1ximo 10 MB.');

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      setParsedImport(parseMetasUlWorkbook(workbook));
    } catch (error: any) {
      setParsedImport({ entries: [], errors: [error?.message || 'N\u00e3o foi poss\u00edvel ler a planilha.'], years: [], periods: [] });
    } finally {
      setIsParsing(false);
    }
  };

  const resetFile = () => {
    setFileName('');
    setParsedImport(null);
    setApiError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImport = async () => {
    if (!parsedImport || parsedImport.errors.length > 0 || parsedImport.entries.length === 0) return;
    setIsImporting(true);
    setApiError('');

    const entries: MetaUlImportEntry[] = parsedImport.entries.map(({ bu, year, month, value }) => ({
      bu,
      year,
      month,
      value,
    }));
    const response = await importMetasUl(entries);
    if (response.success) {
      onImported(response);
    } else {
      setApiError(response.error || 'N\u00e3o foi poss\u00edvel importar as Metas UL.');
    }
    setIsImporting(false);
  };

  const canImport = Boolean(parsedImport?.entries.length) && !parsedImport?.errors.length && !isParsing && !isImporting;

  return (
    <div className="flex max-h-[88vh] min-h-[560px] flex-col overflow-hidden rounded-3xl border border-white/70 bg-slate-50 shadow-2xl">
      <header className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 px-6 py-5 text-white sm:px-8">
        <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-sky-300/20 blur-3xl" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10"><UploadCloud className="h-6 w-6" /></div>
            <div>
              <span className="mb-2 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-50">Importa&ccedil;&atilde;o em lote</span>
              <h2 className="text-2xl font-bold text-white">Importar Metas UL</h2>
              <p className="mt-1 text-sm text-blue-100">Valide a planilha, confira a pr&eacute;via e importe todos os per&iacute;odos de uma vez.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={isImporting} className="rounded-xl border border-white/10 bg-white/10 p-2.5 text-white/80 hover:bg-white/20 hover:text-white disabled:opacity-50" aria-label="Fechar importa\u00e7\u00e3o"><X className="h-5 w-5" /></button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-7">
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(event) => processFile(event.target.files?.[0])} />

        {!parsedImport && !isParsing ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(event) => { event.preventDefault(); setIsDragging(false); processFile(event.dataTransfer.files?.[0]); }}
            className={`flex min-h-[300px] w-full flex-col items-center justify-center rounded-3xl border-2 border-dashed px-6 text-center transition ${isDragging ? 'border-blue-500 bg-blue-100' : 'border-blue-200 bg-white hover:border-blue-400 hover:bg-blue-50/60'}`}
          >
            <div className="mb-5 rounded-2xl bg-blue-100 p-5 text-blue-700"><FileSpreadsheet className="h-10 w-10" /></div>
            <h3 className="text-lg font-bold text-slate-900">Arraste sua planilha ou clique para selecionar</h3>
            <p className="mt-2 max-w-lg text-sm text-slate-500">Use um arquivo .xlsx ou .xls com os cabe&ccedil;alhos <strong>BU</strong>, <strong>META</strong> e <strong>M&Ecirc;S</strong>.</p>
            <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 px-4 py-2 text-xs font-medium text-blue-700">Exemplo: HC | 200000000 | Out-25</div>
          </button>
        ) : null}

        {isParsing ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-3"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /><p className="text-sm font-semibold text-slate-600">Validando planilha...</p></div>
        ) : null}

        {parsedImport && !isParsing ? (
          <div className="space-y-5">
            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3"><div className="rounded-xl bg-blue-100 p-2.5 text-blue-700"><FileSpreadsheet className="h-5 w-5" /></div><div className="min-w-0"><p className="truncate text-sm font-bold text-slate-900">{fileName}</p><p className="text-xs text-slate-500">Primeira aba da planilha</p></div></div>
              <button type="button" onClick={resetFile} disabled={isImporting} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"><RotateCcw className="h-4 w-4" />Trocar arquivo</button>
            </div>

            {parsedImport.errors.length > 0 ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                <div className="mb-3 flex items-center gap-2 font-bold text-red-700"><AlertCircle className="h-5 w-5" />{parsedImport.errors.length} {parsedImport.errors.length === 1 ? 'problema encontrado' : 'problemas encontrados'}</div>
                <ul className="max-h-44 space-y-1 overflow-y-auto pl-7 text-sm text-red-700">{parsedImport.errors.slice(0, 100).map((error, index) => <li key={`${error}-${index}`} className="list-disc">{error}</li>)}</ul>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Registros</p><p className="mt-1 text-2xl font-bold text-blue-900">{parsedImport.entries.length}</p></div>
                  <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Per&iacute;odos</p><p className="mt-1 text-2xl font-bold text-blue-900">{parsedImport.periods.length}</p></div>
                  <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Anos</p><p className="mt-1 text-lg font-bold text-blue-900">{parsedImport.years.join(', ')}</p></div>
                </div>

                <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"><div className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /><div><p className="font-bold">Planilha validada e pronta para importa&ccedil;&atilde;o.</p><p className="mt-1 text-xs text-blue-700">{totalsByYear.map(([year, count]) => `${year}: ${count} registros`).join(' | ')}</p></div></div></div>

                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3"><div className="flex items-center gap-2 text-sm font-bold text-slate-800"><CalendarRange className="h-4 w-4 text-blue-600" />Pr&eacute;via dos dados</div><span className="text-xs text-slate-500">{parsedImport.entries.length} linhas</span></div>
                  <div className="max-h-72 overflow-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="sticky top-0 bg-white shadow-sm"><tr><th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">Linha</th><th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">BU</th><th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">M&ecirc;s</th><th className="px-4 py-3 text-right text-xs font-semibold uppercase text-slate-500">Meta</th></tr></thead>
                      <tbody className="divide-y divide-slate-100">{parsedImport.entries.map((entry) => <tr key={`${entry.year}-${entry.month}-${entry.bu}`} className="hover:bg-blue-50/40"><td className="px-4 py-3 text-xs text-slate-400">{entry.sourceRow}</td><td className="px-4 py-3"><span className="rounded-lg bg-blue-700 px-2.5 py-1 text-xs font-bold text-white">{entry.bu}</span></td><td className="px-4 py-3 text-sm font-medium text-slate-700">{entry.periodLabel}</td><td className="px-4 py-3 text-right text-sm font-bold text-slate-900">{formatCurrency(entry.value)}</td></tr>)}</tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {apiError ? <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700"><AlertCircle className="h-5 w-5" />{apiError}</div> : null}
          </div>
        ) : null}
      </div>

      <footer className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
        <button type="button" onClick={onClose} disabled={isImporting} className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancelar</button>
        <button type="button" onClick={handleImport} disabled={!canImport} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">{isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}{isImporting ? 'Importando...' : `Importar ${parsedImport?.entries.length || 0} registros`}</button>
      </footer>
    </div>
  );
};
